import { useMemo } from "react";
import { useDebouncedSearch } from "@/lib/hooks/search/use-debounced-search";
import { useContacts, useClientSearch } from "@/lib/hooks/use-api";

/**
 * Hook to manage client select data (search, options, loading state).
 * Only used when a client-select field exists in the form.
 */
export function useClientSelectData(hasClientSelectField: boolean) {
  // Search state for client selection
  const { searchValue, debouncedSearchValue, setSearchValue } = useDebouncedSearch({
    initialValue: "",
    delay: 300,
  });

  // Client search hooks - only call if needed
  const { data: initialClients, isLoading: initialClientsLoading } = useContacts({
    isActive: true,
    limit: 20,
  });
  const { data: searchResults, isFetching: isSearching } = useClientSearch(debouncedSearchValue);

  const clientOptions = useMemo(() => {
    if (!hasClientSelectField) return [];
    return debouncedSearchValue ? searchResults : initialClients;
  }, [debouncedSearchValue, searchResults, initialClients, hasClientSelectField]);

  const clientsLoading = initialClientsLoading || isSearching;

  return {
    clientOptions,
    clientsLoading,
    searchValue,
    setSearchValue,
    isSearching,
  };
}
